import { useState } from 'react';
import { motion } from 'framer-motion';
import { Settings, CreditCard, LogOut, User, ChevronDown, Shield, HelpCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import {
  Dropdown,
  DropdownMenu,
  DropdownItem,
  DropdownDivider,
  useDropdown,
} from '../inputs/Dropdown';

function ProfileTrigger() {
  const { isOpen, setIsOpen } = useDropdown();
  const { user } = useAuth();
  const [avatarError, setAvatarError] = useState(false);

  const initials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .toUpperCase()
        .slice(0, 2)
    : 'U';

  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => setIsOpen(!isOpen)}
      className="flex items-center gap-1 p-1 sm:pr-2 rounded-full bg-background hover:bg-background-card border border-border transition-colors"
    >
      {user?.avatar_url && !avatarError ? (
        <img
          src={user.avatar_url}
          alt={user.name}
          className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover"
          referrerPolicy="no-referrer"
          onError={() => setAvatarError(true)}
        />
      ) : (
        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-primary flex items-center justify-center">
          <span className="text-xs sm:text-sm font-medium text-text-inverse">{initials}</span>
        </div>
      )}
      <ChevronDown
        className={`hidden sm:block w-4 h-4 text-text-secondary transition-transform ${isOpen ? 'rotate-180' : ''}`}
      />
    </motion.button>
  );
}

function UserHeader() {
  const { user } = useAuth();
  const [avatarError, setAvatarError] = useState(false);

  return (
    <div className="px-4 py-3 border-b border-border">
      <div className="flex items-center gap-3">
        {user?.avatar_url && !avatarError ? (
          <img
            src={user.avatar_url}
            alt={user.name}
            className="w-10 h-10 rounded-full object-cover"
            referrerPolicy="no-referrer"
            onError={() => setAvatarError(true)}
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center">
            <User className="w-5 h-5 text-text-inverse" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary truncate">
            {user?.name}
          </p>
          <p className="text-xs text-text-secondary truncate">
            {user?.email}
          </p>
        </div>
      </div>
    </div>
  );
}

export function ProfileMenu() {
  const { logout } = useAuth();
  const { isAdmin } = usePermissions();
  const navigate = useNavigate();

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <Dropdown>
      <ProfileTrigger />
      <DropdownMenu align="right" className="w-64" maxHeight="none">
        <UserHeader />
        <div className="py-1">
          {isAdmin && (
            <DropdownItem
              icon={<Shield className="w-4 h-4" />}
              onClick={() => handleNavigate('/app/admin')}
              showCheck={false}
            >
              Admin
            </DropdownItem>
          )}
          <DropdownItem
            icon={<Settings className="w-4 h-4" />}
            onClick={() => handleNavigate('/app/settings')}
            showCheck={false}
          >
            Settings
          </DropdownItem>
          <DropdownItem
            icon={<CreditCard className="w-4 h-4" />}
            onClick={() => handleNavigate('/app/billing')}
            showCheck={false}
          >
            Billing
          </DropdownItem>
          <DropdownItem
            icon={<HelpCircle className="w-4 h-4" />}
            onClick={() => handleNavigate('/app/contact')}
            showCheck={false}
          >
            Support
          </DropdownItem>
        </div>
        <DropdownDivider />
        <div className="py-1">
          <DropdownItem
            icon={<LogOut className="w-4 h-4" />}
            onClick={handleLogout}
            variant="danger"
            showCheck={false}
          >
            Log out
          </DropdownItem>
        </div>
      </DropdownMenu>
    </Dropdown>
  );
}
